import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, User, Users, Bell, AlertTriangle, Clock } from 'lucide-react';
import { crmApi } from './crmApi';
import { CrmScopeContext } from './CrmLayout';

const useOutside = (ref, onOut) => {
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onOut(); }; document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h); }, [ref, onOut]);
};

/* 4B — header search: leads / companies / contacts, owner-scoped on the server. */
export const CrmSearch = () => {
  const navigate = useNavigate();
  const { owner } = useContext(CrmScopeContext);
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  useOutside(box, () => setOpen(false));
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    setBusy(true);
    const t = setTimeout(() => crmApi.search(q.trim(), owner).then(d => { setRes(d); setOpen(true); }).catch(() => setRes({ error: true })).finally(() => setBusy(false)), 250);
    return () => clearTimeout(t);
  }, [q, owner]);
  const go = (to) => { setOpen(false); setQ(''); navigate(to); };
  const total = res && !res.error ? res.leads.length + res.companies.length + res.contacts.length : 0;
  const Group = ({ title, icon: Icon, items, render, to, testid }) => items.length ? (
    <div className="py-1">
      <div className="px-3 py-1 text-[10px] font-bold tracking-widest text-slate-400 uppercase">{title}</div>
      {items.map(it => <button key={it.id} onClick={() => go(to(it))} className="w-full text-left px-3 py-2 hover:bg-violet-50 flex items-center gap-2.5 text-sm" data-testid={testid}><Icon className="h-4 w-4 text-violet-600 shrink-0" />{render(it)}</button>)}
    </div>) : null;
  return (
    <div ref={box} className="relative flex-1 min-w-0 sm:min-w-[140px] max-w-md hidden sm:block" data-testid="crm-search">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input value={q} onChange={e => setQ(e.target.value)} onFocus={() => res && setOpen(true)} onKeyDown={e => e.key === 'Escape' && setOpen(false)} placeholder="Search leads, companies, contacts…"
        className="h-9 w-full rounded-full border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300 focus:bg-white transition-colors" data-testid="crm-search-input" aria-label="Search CRM" />
      {open && res && (
        <div className="absolute mt-1.5 w-full bg-white rounded-xl border border-slate-200 shadow-lg z-50 max-h-[60vh] overflow-y-auto" data-testid="crm-search-results">
          {res.error ? <div className="px-3 py-4 text-sm text-rose-700" data-testid="search-error">Search failed — please try again.</div>
            : total === 0 ? <div className="px-3 py-4 text-sm text-slate-500" data-testid="search-empty">No results for “{q.trim()}” in your CRM records.</div>
            : (<>
              <Group title="Leads" icon={Users} items={res.leads} to={l => `/crm/leads/${l.id}`} testid="search-result-lead" render={l => <span><span className="font-medium text-slate-900">{l.company_name}</span><span className="text-slate-500"> · {l.contact_name} · {l.status}</span></span>} />
              <Group title="Companies" icon={Building2} items={res.companies} to={c => `/crm/companies/${c.id}`} testid="search-result-company" render={c => <span><span className="font-medium text-slate-900">{c.name}</span>{(c.city || c.state) && <span className="text-slate-500"> · {[c.city, c.state].filter(Boolean).join(', ')}</span>}</span>} />
              <Group title="Contacts" icon={User} items={res.contacts} to={c => `/crm/companies/${c.company_id}`} testid="search-result-contact" render={c => <span><span className="font-medium text-slate-900">{c.name}</span><span className="text-slate-500"> · {c.company_name}{c.email ? ` · ${c.email}` : ''}</span></span>} />
            </>)}
          {busy && <div className="px-3 py-1.5 text-[11px] text-slate-400 border-t border-slate-100">Searching…</div>}
        </div>
      )}
    </div>
  );
};

/* 4A — reminder bell: computed live from planned follow-ups (no reminder store → no duplicates, always reflects the CRM record). */
export const CrmReminderBell = () => {
  const navigate = useNavigate();
  const { owner } = useContext(CrmScopeContext);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useOutside(box, () => setOpen(false));
  useEffect(() => {
    const load = () => crmApi.reminders(owner).then(setData).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    const onChange = () => load();
    window.addEventListener('crm:changed', onChange);
    return () => { clearInterval(t); window.removeEventListener('crm:changed', onChange); };
  }, [owner]);
  const n = data?.counts.total || 0;
  const overdue = data?.counts.overdue || 0;
  const Row = ({ a }) => (
    <button onClick={() => { setOpen(false); navigate(`/crm/follow-up/${a.id}`); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2.5" data-testid={`reminder-${a.status === 'Overdue' ? 'overdue' : 'due'}`}>
      {a.status === 'Overdue' ? <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" /> : <Clock className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />}
      <span className="text-sm"><span className="font-medium text-slate-900">{a.type}</span> · {a.company_name}<span className="block text-xs text-slate-500">{a.status === 'Overdue' ? 'Overdue since ' : 'Today at '}{new Date(a.scheduled_at).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span></span>
    </button>
  );
  return (
    <div ref={box} className="relative">
      <button onClick={() => setOpen(v => !v)} className="relative h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" aria-label="Follow-up reminders" data-testid="crm-reminder-bell">
        <Bell className="h-4.5 w-4.5 text-slate-600" style={{ width: 18, height: 18 }} />
        {n > 0 && <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${overdue ? 'bg-rose-600' : 'bg-amber-500'}`} data-testid="crm-reminder-count">{n > 99 ? '99+' : n}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-80 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden" data-testid="crm-reminder-panel">
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between"><span className="text-sm font-semibold text-slate-800">Follow-up reminders</span><span className="text-xs text-slate-500" data-testid="crm-reminder-summary">{overdue} overdue · {data?.counts.due_today || 0} due today</span></div>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-slate-50">
            {n === 0 && <div className="px-3 py-6 text-center text-sm text-slate-500" data-testid="crm-reminder-empty">Nothing due — you're all caught up.</div>}
            {data?.overdue.map(a => <Row key={a.id} a={a} />)}
            {data?.due_today.map(a => <Row key={a.id} a={a} />)}
          </div>
          {n > 0 && <button onClick={() => { setOpen(false); navigate('/crm'); }} className="w-full text-center text-xs text-violet-700 hover:bg-violet-50 py-2 border-t border-slate-100" data-testid="crm-reminder-open-myday">Open My Day to mark them done</button>}
        </div>
      )}
    </div>
  );
};
